import { isEmpty } from 'lodash-es';
import axios       from '@/axios';
import Parameter   from '@/models/Parameter';

export default class FormPrototype
{
    // ------------------------------------------------------------------------- CLASS MEMBERS

    //#region Prototype Attributes

    /**
     * The full code of the prototype.
     * @type {?string}
     */
    code = null;

    /**
     * The code of the model.
     * @type {?string}
     */
    model_code = null;

    /**
     * The code of the variant.
     * @type {?string}
     */
    variant_code = null;

    /**
     * The version of the prototype.
     * @type {?string}
     */
    version = null;

    /**
     * The display label of the prototype.
     * @type {?string}
     */
    label = null;

    /**
     * The display label of the model.
     * @type {?string}
     */
    model_label = null;

    /**
     * @type {?number}
     */
    sort_order = null;

    //#endregion

    /**
     * The parameters of the prototype.
     * @type {Map<string, Parameter>}
     */
    parameters;

    /**
     * Whether the form is being templated.
     * @var {boolean}
     */
    templating = false;

    /**
     * Whether the form is being validated.
     * @type {boolean}
     */
    validating = false;

    /**
     * Whether the prototype has a calculator.
     * @type {boolean}
     */
    hasCalculator = false;

    /**
     * Whether the OFG calculator is running.
     * @type {boolean}
     */
    calculatingOfg = false;

    /**
     * @type {AbortController}
     */
    abortController = null;

    /**
     * Whether the form is based on an existing order.
     * @type {boolean}
     */
    isReOrder = false;

    /**
     * If the form is based on an existing order, here
     * are the values of the previous lens.
     * @type {Record<string, any>}
     */
    previousValues = {};

    /**
     * Set to `true` if prototype has been templated once.
     * @type {boolean}
     */
    initialized = false;


    // ------------------------------------------------------------------------- CONSTRUCTOR

    constructor(attributes = {})
    {
        console.log('PROTO CREATED WITH ATTRIBUTES: ', attributes);

        this.parameters = new Map();

        if(attributes)
        {
            this.setAttributes(attributes);
        }
    }


    // ------------------------------------------------------------------------- PROTOTYPE ATTRIBUTES

    setAttributes(attributes)
    {
        console.log('PROTO RE-SET');

        for(const key in attributes)
        {
            if(this[key] !== undefined)
            {
                this[key] = attributes[key];
            }
        }

        if(attributes.calculator_class)
        {
            this.hasCalculator = true;
        }
    }


    // ------------------------------------------------------------------------- STATUS

    isInitialized()
    {
        return this.initialized;
    }


    // ------------------------------------------------------------------------- PARAMETERS

    /**
     *
     * @param {string}              eid          The entity ID.
     * @param {Record<string, any>} [initValues] The values to template. If an
     *                                           empty object is used, the
     *                                           current prototype's parameter
     *                                           values will be used instead.
     * @param {string}              viewAs       `"CUSTOMER"` to view as such.
     * @returns
     */
    template(eid, initValues = {}, viewAs)
    {
        if(typeof viewAs !== 'string')
        {
            throw new TypeError('FormPrototype.template(): missing required parameter `viewAs`');
        }

        let values;

        // Set "templating" status
        this.templating = true;

        // If a request was already ongoing, abort it
        if(this.abortController)
        {
            this.abortController.abort();
        }

        // Template with given values (on intialization) or with parameter values.
        if(isEmpty(initValues))
        {
            values = this.getValues();
            console.log('TEMPLATE WITH PROTOTYPE VALUES', values);
        }
        else
        {
            values = initValues;
            console.log('TEMPLATE WITH INITIAL VALUES', values);
        }

        // Build URL
        const query = new URLSearchParams();
        if(viewAs !== '')
        {
            query.append('as', viewAs);
        }
        if(values.DESIGN_LEVEL)
        {
            query.append('level', values.DESIGN_LEVEL);
        }

        const url = '/api/prototype/template/:eid/:prototypeCode'
            .replace(':eid', eid)
            .replace(':prototypeCode', this.code)
            .concat(query.size ? `?${query.toString()}` : '');

        // Get parameter values
        const data = {
            values,
            old_values:      this.getOldValues(),
            previous_values: this.previousValues,
        };

        // Create AbortController
        this.abortController = new AbortController();

        // Run query
        return axios.post(url, data, { signal: this.abortController.signal })
            .then(response =>
            {
                // Reset AbortController
                this.abortController = null;

                // Set parameters (template).
                this.setParameters(response.data);

                // If initial values have been given => set parameter values with them.
                if(initValues)
                {
                    this.setValues(initValues);
                }

                // For all parameters => set old value from value (must be after this.setParameters())
                this.setOldValuesFromValues();

                // Set as initialized.
                this.initialized = true;

                return response;
            })
            .catch(error =>
            {
                if(!axios.isCancel(error))
                {
                    console.log(error);

                    throw error;
                }
            })
            .finally(() =>
            {
                this.templating = false;
            });
    }

    setParameters(parameters)
    {
        const codes = [];

        // Create parameter instance and add it to parameters map (if not already exists) or update existing parameter instance.
        parameters.forEach(parameter =>
        {
            codes.push(parameter.code);

            if(this.parameters.has(parameter.code))
            {
                this.parameters.get(parameter.code).setProperties(parameter);
            }
            else
            {
                this.parameters.set(parameter.code, new Parameter(parameter));
            }
        });

        // Delete parameter instance that does not exist anymore (regarding given "parameters" arguments).
        this.parameters.forEach((parameter, key) =>
        {
            if(!codes.includes(key))
            {
                this.parameters.delete(key);
            }
        });
    }

    getParametersAsArray()
    {
        return Array.from(this.parameters.values());
    }

    getParameters()
    {
        return this.parameters;
    }

    getParameterByCode(parameterCode)
    {
        return this.parameters.has(parameterCode) ? this.parameters.get(parameterCode) : null;
    }

    hasParameter(parameterCode)
    {
        return this.parameters.has(parameterCode);
    }


    // ------------------------------------------------------------------------- VALUES

    /**
     * @param {Record<string, any>} parameterValues
     * @param {?object}             defaults Default properties to use if the
     *                                       parameter doesn't already exist.
     */
    setValues(parameterValues, defaults = null)
    {
        for(const parameterCode in parameterValues)
        {
            this.setValue(parameterCode, parameterValues[parameterCode], defaults);
        }
    }

    /**
     * @param {string}  parameterCode
     * @param {any}     parameterValue
     * @param {?object} defaults Default properties to use if the parameter
     *                           doesn't already exists.
     */
    setValue(parameterCode, parameterValue, defaults = null)
    {
        if(this.parameters.has(parameterCode))
        {
            this.parameters.get(parameterCode).setValue(parameterValue);
        }
        else if(defaults !== null)
        {
            const param = new Parameter({
                ...defaults,
                code:  parameterCode,
                value: parameterValue,
            });
            this.parameters.set(parameterCode, param);
        }
    }

    /**
     * @param {boolean} nonNullValueOnly
     * @returns {Record<string, any>}
     */
    getValues(nonNullValueOnly = false)
    {
        const values = {};
        this.parameters.forEach((parameter, key) =>
        {
            const value = parameter.getValue();
            if(!nonNullValueOnly || value !== null)
            {
                values[key] = value;
            }
        });

        return values;
    }

    getValue(parameterCode)
    {
        if(this.parameters.has(parameterCode))
        {
            return this.parameters.get(parameterCode).getValue();
        }

        return null;
    }


    // ------------------------------------------------------------------------- OLD VALUES

    setOldValuesFromValues()
    {
        this.parameters.forEach(parameter =>
        {
            parameter.setOldValueFromValue();
        });
    }

    /**
     * Null old values are omitted: before the first template() call every old
     * value is null, and an empty object is how CS Designer recognizes that
     * there is no baseline yet to compare the current values against.
     *
     * @returns {Record<string, any>}
     */
    getOldValues()
    {
        const oldValues = {};
        this.parameters.forEach((parameter, key) =>
        {
            const oldValue = parameter.getOldValue();
            if(oldValue !== null)
            {
                oldValues[key] = oldValue;
            }
        });

        return oldValues;
    }


    // ------------------------------------------------------------------------- PREVIOUS VALUES

    setPreviousValues(previousValues)
    {
        this.previousValues = previousValues;
        this.isReOrder = true;
    }

    getPreviousValues()
    {
        return this.previousValues;
    }

    hasPreviousValues()
    {
        return this.previousValues.length > 0;
    }


    // ------------------------------------------------------------------------- VALIDATION

    validate(eid, options = {}, viewAs = '')
    {
        return new Promise((resolve, reject) =>
        {
            // Set "validating" status
            this.validating = true;

            const values = this.getValues();

            // Build URL
            const query = new URLSearchParams();
            if(viewAs !== '')
            {
                query.append('as', viewAs);
            }
            if(values.DESIGN_LEVEL)
            {
                query.append('level', values.DESIGN_LEVEL);
            }

            const url = '/api/prototype/validate/:eid/:prototypeCode'
                .replace(':eid', eid)
                .replace(':prototypeCode', this.code)
                .concat(query.size ? `?${query.toString()}` : '');

            // Get parameter values
            const data = {
                values,
                previous_values: this.previousValues,
                options,
            };

            // Run query
            axios.post(url, data)
                .then(response =>
                {
                    this.clearErrors();
                    resolve(response);
                })
                .catch(error =>
                {
                    this.clearErrors();
                    this.setErrors(error.response.errors);

                    console.log('ERRORS:', error.response.errors);
                    reject(error);
                })
                .finally(() =>
                {
                    this.validating = false;
                });
        });
    }

    /**
     * Set the first error of each parameter.
     * Parameter codes which aren't a part of this prototype are skipped.
     *
     * @param {Record<string, string[]>} errors The parameter errors, with param
     *                                          code as the key and the list of
     *                                          errors as the value.
     */
    setErrors(errors)
    {
        for(const parameterCode in errors)
        {
            if(this.parameters.has(parameterCode))
            {
                this.parameters.get(parameterCode).setError(errors[parameterCode][0]);
            }
        }
    }

    getErrorByParameterCode(parameterCode)
    {
        if(this.parameters.has(parameterCode))
        {
            return this.parameters.get(parameterCode).getError();
        }

        return null;
    }

    hasErrors()
    {
        for(const [code, param] of this.parameters)
        {
            if(param.error)
            {
                return true;
            }
        }

        return false;
    }

    clearErrors()
    {
        this.parameters.forEach(parameter =>
        {
            parameter.clearError();
        });
    }


    // ------------------------------------------------------------------------- TEMPLATE AND VALIDATE

    /**
     * Template the prototype and validate the values in one request.
     *
     * @param {string}        eid
     * @param {Object}        options
     * @param {boolean}       options.only_present
     * @param {boolean}       options.no_required
     * @param {""|"CUSTOMER"} viewAs
     * @returns
     */
    templateAndValidate(eid, options, viewAs)
    {
        if(typeof viewAs !== 'string')
        {
            throw new TypeError('FormPrototype.templateAndValidate(): missing required parameter `viewAs`');
        }

        // Set "templating" and "validating" status
        this.templating = true;
        this.validating = true;

        // If a request was already ongoing, abort it
        if(this.abortController)
        {
            this.abortController.abort();
        }

        // Retrieve values
        const values = this.getValues();
        console.log('TEMPLATE & VALIDATE WITH PROTOTYPE VALUES', values);

        // Build URL
        const query = new URLSearchParams();
        if(viewAs !== '')
        {
            query.append('as', viewAs);
        }
        if(values.DESIGN_LEVEL)
        {
            query.append('level', values.DESIGN_LEVEL);
        }

        const url = '/api/prototype/template-validate/:eid/:prototypeCode'
            .replace(':eid', eid)
            .replace(':prototypeCode', this.code)
            .concat(query.size ? `?${query.toString()}` : '');

        // Get parameter values
        const data = {
            values,
            old_values:      this.getOldValues(),
            previous_values: this.previousValues,
            options,
        };

        // Create AbortController
        this.abortController = new AbortController();

        // Run query
        return axios.post(url, data, { signal: this.abortController.signal })
            .then(response =>
            {
                // Reset AbortController
                this.abortController = null;

                // Set parameters (template).
                this.setParameters(response.data.template);

                // For all parameters => set old value from value (must be after this.setParameters())
                this.setOldValuesFromValues();

                // Suppress all error messages
                this.clearErrors();

                // If there is error messages
                if(response.data.validation)
                {
                    // Set error messages
                    this.setErrors(response.data.validation);
                }

                return response;
            })
            .catch(error =>
            {
                if(!axios.isCancel(error))
                {
                    console.log(error);

                    throw error;
                }
            })
            .finally(() =>
            {
                this.templating = false;
                this.validating = false;
            });
    }


    // ------------------------------------------------------------------------- CONVERT

    /**
     * Convert the parameters used by this prototype to those used by another.
     *
     * @param {string}              eid    The hashed ID of the entity.
     * @param {string}              target The code of the prototype to convert to.
     * @param {Record<string, any>} values The values.
     * @returns {Promise<Record<string, any>>}
     */
    async convert(eid, target, values)
    {
        const url = '/api/prototype/convert/{eid}/{source}/{target}'
            .replace('{eid}', eid)
            .replace('{source}', this.code)
            .replace('{target}', target);
        const { data: converted } = await axios.post(url, { values });
        return converted;
    }

    // ------------------------------------------------------------------------- CALCULATE OFG

    /**
     * Calculate the OFG.
     * This should obviously be called only if the form is an OFG.
     *
     * @param {string} eid The hashed ID of the entity.
     * @returns {Promise<Record<string, any>>}
     */
    calculateOfg(eid)
    {
        const url = '/api/prototype/calculate/{eid}/{prototypeCode}'
            .replace('{eid}', eid)
            .replace('{prototypeCode}', this.code);
        const data = { values: this.getValues() };

        return new Promise((resolve, reject) =>
        {
            if(this.calculatingOfg)
            {
                if(!this.abortController)
                {
                    return;
                }

                this.abortController.abort();
            }

            this.calculatingOfg = true;
            this.abortController = new AbortController();
            axios.post(url, data, { signal: this.abortController.signal })
                .then(({ data: result }) => resolve(result))
                .catch(error =>
                {
                    if(!axios.isCancel(error))
                    {
                        this.clearErrors();
                        this.setErrors(error.response.errors);
                    }

                    reject(error);
                })
                .finally(() =>
                {
                    this.calculatingOfg = false;
                    this.abortController = null;
                });
        });
    }
}
